import { useMemo } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/lib/format-utils";
import { type SortKey, type SortDir, sortActivityUsers, getActivityTotal } from "@/lib/activity-sort-utils";
import type { PeriodActivityUser } from "@/lib/api/admin";

type GridRow = PeriodActivityUser;

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "userName", label: "User" },
  { key: "epicRequestsCreated", label: "Requests" },
  { key: "epicsCreated", label: "Epics Created" },
  { key: "featuresCompleted", label: "Features Done" },
  { key: "tasksCompleted", label: "Tasks Done" },
  { key: "epicsCompleted", label: "Epics Done" },
  { key: "total", label: "Total" },
  { key: "totalDurationMinutes", label: "Time Invested" },
  { key: "avgDurationPerItem", label: "Avg Time/Item" },
  { key: "aiSessionMinutes", label: "AI Session Time" },
];

interface SortIconProps {
  columnKey: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
}

function SortIcon({ columnKey, sortKey, sortDir }: SortIconProps) {
  if (columnKey !== sortKey) {
    return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
  }
  return sortDir === "asc" ? (
    <ChevronUp className="h-3 w-3" />
  ) : (
    <ChevronDown className="h-3 w-3" />
  );
}

interface DailyActivityGridProps {
  data: GridRow[];
  sortKey: SortKey;
  sortDir: SortDir;
  onSortChange: (key: SortKey, dir: SortDir) => void;
  onUserClick: (userId: string) => void;
}

export function DailyActivityGrid({ data, sortKey, sortDir, onSortChange, onUserClick }: DailyActivityGridProps) {
  function handleSort(key: SortKey) {
    if (key === sortKey) {
      onSortChange(key, sortDir === "asc" ? "desc" : "asc");
    } else {
      onSortChange(key, "desc");
    }
  }

  const { sorted, maxTotal } = useMemo(() => {
    let best = 0;
    for (const row of data) {
      const t = getActivityTotal(row);
      if (t > best) best = t;
    }

    const rows = sortActivityUsers(data, sortKey, sortDir);

    return { sorted: rows, maxTotal: best };
  }, [data, sortKey, sortDir]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={cn(
                  "py-3 text-left font-medium text-muted-foreground select-none whitespace-nowrap",
                  col.key === "userName" ? "pl-0 pr-4 min-w-[180px]" : "px-4 text-right"
                )}
              >
                <button
                  type="button"
                  onClick={() => handleSort(col.key)}
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  {col.label}
                  <SortIcon columnKey={col.key} sortKey={sortKey} sortDir={sortDir} />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const total = getActivityTotal(row);
            const hasTimeActivity = row.totalDurationMinutes > 0 || row.aiSessionMinutes > 0;
            const isZero = total === 0 && !hasTimeActivity;
            const isTopPerformer = maxTotal > 0 && total === maxTotal;

            return (
              <tr
                key={row.userId}
                tabIndex={0}
                role="button"
                onClick={() => onUserClick(row.userId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onUserClick(row.userId);
                  }
                }}
                className={cn(
                  "border-b border-white/5 cursor-pointer hover:bg-white/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors",
                  !isZero && !isTopPerformer && "even:bg-muted/50",
                  isZero && "bg-amber-500/[0.06]",
                  isTopPerformer && "bg-emerald-500/[0.06]"
                )}
              >
                <td className="py-3 pr-4">
                  <div className={cn("font-medium truncate max-w-[200px]", isTopPerformer && "text-emerald-400")}>
                    {row.userName}
                  </div>
                  <div className="text-xs text-muted-foreground truncate max-w-[200px]">{row.userEmail}</div>
                </td>
                <td className="py-3 px-4 text-right tabular-nums">{row.epicRequestsCreated}</td>
                <td className="py-3 px-4 text-right tabular-nums">{row.epicsCreated}</td>
                <td className="py-3 px-4 text-right tabular-nums">{row.featuresCompleted}</td>
                <td className="py-3 px-4 text-right tabular-nums">{row.tasksCompleted}</td>
                <td className="py-3 px-4 text-right tabular-nums">{row.epicsCompleted}</td>
                <td className={cn("py-3 px-4 text-right tabular-nums font-semibold", isTopPerformer && "text-emerald-400")}>
                  {total}
                  {isZero && (
                    <AlertTriangle className="ml-1.5 inline-block h-3.5 w-3.5 text-amber-400" aria-label="No activity recorded" />
                  )}
                </td>
                <td className="py-3 px-4 text-right tabular-nums">{formatDuration(row.totalDurationMinutes)}</td>
                <td className="py-3 px-4 text-right tabular-nums">{formatDuration(row.avgDurationPerItem)}</td>
                <td className="py-3 px-4 text-right tabular-nums">{formatDuration(row.aiSessionMinutes)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
